import { useEffect, useRef } from 'react'

import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'
import {
  BadgeButton,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ankor-io/ui-components'

import { useInfiniteOrganizationVesselsQuery } from '@/queries'

import { type CardsTableConnectedAccount } from './connected-account-cards'

const VESSELS_PER_PAGE = 20

/** id of the disclosure region the account badges expand, shared with the page-level wrapper. */
export const VESSEL_CARDS_SECTION_ID = 'vessel-cards-section'

export const OrganizationVesselsAccounts = ({
  organizationId,
  onConnectedAccountClick,
  selectedVesselId,
  selectedConnectedAccountId,
}: {
  organizationId: string
  onConnectedAccountClick: (vesselId: string, connectedAccount: CardsTableConnectedAccount) => void
  selectedVesselId?: string | null
  selectedConnectedAccountId?: string | null
}) => {
  const { data, isPending, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteOrganizationVesselsQuery(
      {
        organizationId,
        enabled: Boolean(organizationId),
      },
      VESSELS_PER_PAGE,
    )

  const vessels = data?.pages.flatMap((page) => page.vessels) ?? []

  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0]
        if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1, rootMargin: '100px' },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  if (isPending) {
    return <Loading variant="spinner" />
  }
  if (isError) {
    return <div>Error: {error.message}</div>
  }
  if (vessels.length === 0) {
    return <div>No vessels found</div>
  }
  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Vessel Name</TableHeader>
            <TableHeader>Connected Account APA</TableHeader>
            <TableHeader>Connected Account OPS</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {vessels.map((entry) => {
            const apaAccount = entry.connectedAccounts.find((c) => c.type === AwxConnectedAccountType.APA)
            const opsAccount = entry.connectedAccounts.find((c) => c.type === AwxConnectedAccountType.OPERATIONS)
            const isSelected = (accountId?: string) =>
              !!accountId && entry.vessel.id === selectedVesselId && accountId === selectedConnectedAccountId

            return (
              <TableRow key={entry.vessel.id}>
                <TableCell>{entry.vessel.name}</TableCell>
                <TableCell>
                  {apaAccount ? (
                    <BadgeButton
                      color={isSelected(apaAccount.id) ? 'primary' : 'gray'}
                      aria-expanded={isSelected(apaAccount.id)}
                      aria-controls={VESSEL_CARDS_SECTION_ID}
                      onClick={() => onConnectedAccountClick(entry.vessel.id, apaAccount)}
                    >
                      {apaAccount.id}
                    </BadgeButton>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-600">--</span>
                  )}
                </TableCell>
                <TableCell>
                  {opsAccount ? (
                    <BadgeButton
                      color={isSelected(opsAccount.id) ? 'primary' : 'gray'}
                      aria-expanded={isSelected(opsAccount.id)}
                      aria-controls={VESSEL_CARDS_SECTION_ID}
                      onClick={() => onConnectedAccountClick(entry.vessel.id, opsAccount)}
                    >
                      {opsAccount.id}
                    </BadgeButton>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-600">--</span>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <div
        ref={sentinelRef}
        className="h-20 flex items-center justify-center"
      >
        {isFetchingNextPage && <Loading variant="dots" />}
      </div>
    </>
  )
}
export default OrganizationVesselsAccounts
